import React, { useState } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import toast from 'react-hot-toast';
import SafeIcon from '../../common/SafeIcon';
import { FiSave } from 'react-icons/fi';


const CallSettings = () => {
  const { settings, updateSettings } = useSettings();
  const [formData, setFormData] = useState({
    callSettings: {
      defaultVoice: settings.callSettings?.defaultVoice || 'natural',
      recordCalls: settings.callSettings?.recordCalls ?? true,
      transcribeConversations: settings.callSettings?.transcribeConversations ?? true
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    updateSettings(formData);
    toast.success('Call settings saved successfully');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      callSettings: {
        ...prev.callSettings,
        [name]: type === 'checkbox' ? checked : value
      }
    }));
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">AI Call Configuration</h3>
        <p className="text-gray-600">
          Configure settings for AI-powered calling through Thoughtly integration.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Default Voice
          </label>
          <select
            name="defaultVoice"
            value={formData.callSettings.defaultVoice}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="natural">Natural (Recommended)</option>
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="authoritative">Authoritative</option>
          </select>
          <p className="mt-2 text-sm text-gray-500">
            Choose the voice style for your AI calls.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="recordCalls"
              name="recordCalls"
              checked={formData.callSettings.recordCalls}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="recordCalls" className="ml-3 text-sm text-gray-700">
              Record all calls
            </label>
          </div>
          <p className="text-sm text-gray-500 ml-6">
            Automatically record all AI calls for quality assurance and training.
          </p>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="transcribeConversations"
              name="transcribeConversations"
              checked={formData.callSettings.transcribeConversations}
              onChange={handleChange}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="transcribeConversations" className="ml-3 text-sm text-gray-700">
              Transcribe conversations
            </label>
          </div>
          <p className="text-sm text-gray-500 ml-6">
            Generate text transcripts of all conversations for easy review and analysis.
          </p>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <h4 className="font-medium text-yellow-900 mb-2">Call Compliance</h4>
          <p className="text-sm text-yellow-700">
            Ensure you comply with local regulations regarding call recording and consent. 
            Some jurisdictions require explicit consent before recording calls.
          </p>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-3">Call Script Templates</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-white rounded border">
              <div>
                <p className="text-sm font-medium text-gray-900">Sales Outreach</p>
                <p className="text-xs text-gray-500">General sales introduction script</p>
              </div>
              <button type="button" className="text-blue-600 hover:text-blue-700 text-sm">
                Edit
              </button>
            </div>
            <div className="flex items-center justify-between p-3 bg-white rounded border">
              <div>
                <p className="text-sm font-medium text-gray-900">Follow-up</p>
                <p className="text-xs text-gray-500">Post-meeting follow-up script</p>
              </div>
              <button type="button" className="text-blue-600 hover:text-blue-700 text-sm">
                Edit
              </button>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="submit"
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiSave} className="text-sm" />
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default CallSettings;